import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Accordion, Button, Col, Modal, Row, Container } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { EventDetailList } from './EventDetailTable'
// @ts-ignore
import ModalImage from 'react-modal-image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLocation, useNavigate } from 'react-router-dom'
import { CoverPhoto, IEvent } from '../../store/events/interfaces'
import { isMobileQuery, isTabletOrSmallDesktopQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { stages } from '../../routes'
import { RegisterButton } from '../shared/RegisterButton'
import truncate from 'html-truncate';
import { InfoBadges } from '../shared/Badges'

export const EventModal = (props: {
  eventId: number
  events: IEvent[]
  editable: boolean
}) => {
  const { eventId, events, editable } = props
  const navigate = useNavigate()

  const location = useLocation()
  const parentStage = stages.find(stage =>
    location.pathname.includes(stage.path),
  )

  const [open, setOpen] = useState(false)

  const { data: program } = useSelector((state: RootState) => state.program)

  const isMobile = useMediaQuery(isMobileQuery)
  const isTabletOrSmallDesktop = useMediaQuery(isTabletOrSmallDesktopQuery)

  const eventIdx = events.findIndex(event => event.event_id === eventId)
  const event = events[eventIdx]

  const descriptionCharactersLimit = isMobile ? 600 : isTabletOrSmallDesktop ? 900 : 1800;
  const truncateDescription = event?.description_html?.length > descriptionCharactersLimit && (event?.description_html?.length - descriptionCharactersLimit) / descriptionCharactersLimit >= 0.6
  const [showFullDescription, setShowFullDescription] = useState(!truncateDescription);

  const nextItem = eventIdx === events.length - 1 ? undefined : events[eventIdx + 1]
  const prevItem = eventIdx === 0 ? undefined : events[eventIdx - 1]

  // Navigate between events
  const goToPrevEvent = useCallback(() => {
    if (prevItem !== undefined && parentStage) {
      navigate(`${parentStage.path}/${prevItem.event_id}`, { replace: true });
    }
  }, [parentStage, prevItem, navigate]);

  const goToNextEvent = useCallback(() => {
    if (nextItem !== undefined && parentStage) {
      navigate(`${parentStage.path}/${nextItem.event_id}`, { replace: true });
    }
  }, [parentStage, nextItem, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevEvent();
      } else if (e.key === 'ArrowRight') {
        goToNextEvent();
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [eventId, events, goToNextEvent, goToPrevEvent]); // Re-add listeners when the event changes

  const textRef = useRef(null);
  const [textHeight, setTextHeight] = useState(0);

  useEffect(() => {
    if (!textRef || !textRef.current) return;

    const observer = new ResizeObserver(() => {
      // @ts-ignore
      setTextHeight(textRef.current.offsetHeight);
    });

    observer.observe(textRef.current);

    return () => observer.disconnect();
  }, []);

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const getTruncatedDescription = (html: string, limit: number) => {
    return truncate(html, limit, { ellipsis: '…', slop: 50 });
  };

  if (event === undefined) {
    return <></>
  } else {
    const descriptionToShow = showFullDescription
      ? event?.description_html
      : getTruncatedDescription(event.description_html, descriptionCharactersLimit)
    const nextItem =
      eventIdx === events.length - 1 ? undefined : events[eventIdx + 1]
    const prevItem = eventIdx === 0 ? undefined : events[eventIdx - 1]

    return (
      <>
        <Modal
          size='xl'
          dialogClassName={!isMobile ? 'modal-90w' : ''}
          show={true}
          animation={false}
          onHide={
            () => {
              navigate(
                `${parentStage?.path}`, { replace: true },
              )
            }
          }
        >
          <Modal.Header closeButton closeLabel={'Modal schließen'}>
            <Modal.Title className='w-100'>
              <strong>{event.name}</strong>
              <div className="float-right"><InfoBadges event={event} tooltipPlacement={"bottom"}/></div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>

            {/* Teaser, Description */}
            <Row className="border-bottom mb-3">
              <Col md={12}>
                {event.teaser ? <p className="lead">{event.teaser}</p> : null}

                {event.cover_photo && program.enable_preview_images && (
                  <div className="event-detail-cover-image-wrapper float-right img-thumbnail ml-1 mb-1">
                    <img
                      className="event-detail-cover-image img-fluid"
                      src={event.cover_photo.medium}
                      style={{ maxHeight: `${textHeight}px`}}
                      alt={
                        event.cover_photo.caption
                          ? event.cover_photo.caption
                          : `Vorschaubild der Veranstaltung ${event.name}`
                      }
                    />
                  </div>
                )}
                <div ref={textRef} >
                  <div
                    className="event-description-dynamic"
                    dangerouslySetInnerHTML={{ __html: descriptionToShow }}
                  />
                  <div
                    className="event-description-full d-none"
                    dangerouslySetInnerHTML={{ __html: event.description_html }}
                  />

                  {!showFullDescription && event.description_html.length > descriptionCharactersLimit && (
                    <Button className="event-description-dynamic mb-2" variant="primary" onClick={toggleDescription}>
                      Weiterlesen
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
            {/* Event Details */}
            <Row>
              <Col md={12}>
                <EventDetailList event={event} />
              </Col>
            </Row>
            {/* Images */}
            {event.photos.length > 0 && (
                <Accordion>
                  <Row>
                    <Col xs={12}>
                      <Accordion.Toggle
                        as={Button}
                        variant='button'
                        eventKey='0'
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        className='btn-info my-2'
                      >
                        <>
                        Bildergalerie öffnen <FontAwesomeIcon icon={open ? 'caret-down' : 'caret-right'} />
                        </>
                      </Accordion.Toggle>
                    </Col>
                  </Row>
                  <Accordion.Collapse eventKey="0">
                    <Container fluid>
                      <Row className='justify-content-between'>
                        {event.photos.map((photo: CoverPhoto, key) => (
                          <Col
                            className="px-0 mx-1 border rounded d-flex flex-column align-items-left"
                            key={
                              event.event_id +
                              `${photo.thumbnail &&
                              photo.thumbnail.split('/').pop()}`
                            }
                          >
                            <ModalImage
                              className='bg-white rounded img-fluid card-img-top modal-image-fix'
                              small={photo.thumbnail}
                              large={photo.medium}
                              alt={
                                photo.caption
                                  ? photo.caption
                                  : `Bild ${key + 1} der Veranstaltung ${event.name}`
                              }
                            />
                            {photo.caption || photo.credits || photo.source ? <div className='pt-1 text-center'><hr className="m-0"/>
                              {photo.caption ? <p className='text-dark px-1'>{photo.caption}</p> : <></>}
                              {photo.credits ? <p className="small m-0 p-0 mb-0 px-1">Urheber: {photo.credits}</p> : <></>}
                              {photo.source ? <p className="small text-muted px-1"><a href={photo.source} target={'_blank'}>Quelle</a></p> : <></>}
                            </div> : <></>}
                          </Col>
                        ))}
                      </Row>
                    </Container>
                  </Accordion.Collapse>
                </Accordion>
            )}
          </Modal.Body>
          <Modal.Footer>
            <div className='w-100'>
              <Row>
                <Col md={6} sm={12} className='d-block d-md-flex'>
                  {prevItem !== undefined && parentStage && (
                    <Button
                      variant='outline-dark'
                      onClick={goToPrevEvent}
                      className='mobile-btn'
                    >
                      <FontAwesomeIcon
                        icon='chevron-left'
                        role='presentation'
                      />{' '}
                      vorherige Veranstaltung
                    </Button>
                  )}
                  {nextItem !== undefined && parentStage && (
                    <Button
                      variant='outline-dark'
                      className='ml-0 ml-md-2 mobile-btn'
                      onClick={goToNextEvent}
                    >
                      Nächste Veranstaltung{' '}
                      <FontAwesomeIcon
                        icon='chevron-right'
                        role='presentation'
                      />
                    </Button>
                  )}
                </Col>
                <Col
                  md={6}
                  sm={12}
                  className='d-block d-md-flex justify-content-end'
                >
                  <RegisterButton editable={editable ?? true} event={event} />
                  <Button
                    variant='secondary'
                    onClick={() => navigate(
                      `${parentStage?.path}`, { replace: true },
                    )}
                    className='mobile-btn ml-0 ml-md-2'
                  >
                    Schließen
                  </Button>
                </Col>
              </Row>
            </div>
          </Modal.Footer>
        </Modal>
      </>
    )
  }
}
